import { publicApi, type ApiClient } from '@/helpers/apiClient'
import {
  allowlistConfirmedDtoSchema,
  allowlistDtoSchema,
  allowlistEntryDtoSchema,
  capturedMailDtoSchema,
  capturedMailListDtoSchema,
  type AllowlistConfirmedDto,
  type AllowlistDto,
  type AllowlistEntryDto,
  type CapturedMailDto,
  type CapturedMailListDto,
} from '@lib/dto/test-mail.dto'
import type { AddAllowlistEntryInput, TestMailMessagesQuery } from '@lib/input/test-mail.input'
import type { Uuid } from '@lib/validators/params'
import type { ITestMailService } from '@/types/TestMailService.types'

function base(tenantId: Uuid): string {
  return `/dashboard/tenants/${encodeURIComponent(tenantId)}/test-mail`
}

/**
 * The mailbox and allowlist, bound to a dashboard session's client. The
 * address-owner confirmation needs no session and goes through `publicApi`.
 */
export class TestMailService implements ITestMailService {
  constructor(private readonly client: ApiClient) {}

  listMessages(tenantId: Uuid, query: TestMailMessagesQuery): Promise<CapturedMailListDto> {
    const search = query.envelopeId ? `?envelopeId=${encodeURIComponent(query.envelopeId)}` : ''
    return this.client.get(`${base(tenantId)}/messages${search}`, capturedMailListDtoSchema)
  }

  getMessage(tenantId: Uuid, messageId: Uuid): Promise<CapturedMailDto> {
    return this.client.get(`${base(tenantId)}/messages/${encodeURIComponent(messageId)}`, capturedMailDtoSchema)
  }

  allowlist(tenantId: Uuid): Promise<AllowlistDto> {
    return this.client.get(`${base(tenantId)}/allowlist`, allowlistDtoSchema)
  }

  addEntry(tenantId: Uuid, input: AddAllowlistEntryInput): Promise<AllowlistEntryDto> {
    return this.client.post(`${base(tenantId)}/allowlist`, input, allowlistEntryDtoSchema)
  }

  verifyEntry(tenantId: Uuid, entryId: Uuid): Promise<AllowlistEntryDto> {
    return this.client.post(`${base(tenantId)}/allowlist/${encodeURIComponent(entryId)}/verify`, {}, allowlistEntryDtoSchema)
  }

  resendConfirmation(tenantId: Uuid, entryId: Uuid): Promise<AllowlistEntryDto> {
    return this.client.post(`${base(tenantId)}/allowlist/${encodeURIComponent(entryId)}/resend`, {}, allowlistEntryDtoSchema)
  }

  async removeEntry(tenantId: Uuid, entryId: Uuid): Promise<void> {
    await this.client.del(`${base(tenantId)}/allowlist/${encodeURIComponent(entryId)}`)
  }

  confirm(token: string): Promise<AllowlistConfirmedDto> {
    return publicApi.post('/test-mail/confirm', { token }, allowlistConfirmedDtoSchema)
  }
}

/** For the public confirmation page, which has no dashboard session. */
export const publicTestMailService = new TestMailService(publicApi)
